import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Clock,
  MessageSquare,
  Tag,
  CheckCircle,
  User,
  FileText,
  UserCheck,
  TrendingUp
} from 'lucide-react';
import { format } from 'date-fns';
import omnichannelService from '../../services/omnichannelService';

interface JourneyEvent {
  id: string;
  event_type:
    | 'conversation_started'
    | 'assignment_changed'
    | 'tag_added'
    | 'tag_removed'
    | 'status_changed'
    | 'note_added'
    | 'note_updated'
    | 'note_deleted'
    | 'customer_info_updated'
    | 'priority_changed';
  title: string;
  description?: string;
  created_at: string;
  created_by_name?: string;
  metadata?: any;
}

interface CustomerJourneyViewProps {
  conversationId: string;
}

export default function CustomerJourneyView({ conversationId }: CustomerJourneyViewProps) {
  const { theme } = useTheme();
  const [events, setEvents] = useState<JourneyEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJourneyEvents();
  }, [conversationId]);

  const loadJourneyEvents = async () => {
    try {
      const data = await omnichannelService.getConversationJourney(conversationId);
      setEvents(data || []);
    } catch (error) {
      console.error('Failed to load journey events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'conversation_started':
        return <MessageSquare className="h-4 w-4" />;
      case 'note_added':
      case 'note_updated':
      case 'note_deleted':
        return <FileText className="h-4 w-4" />;
      case 'tag_added':
      case 'tag_removed':
        return <Tag className="h-4 w-4" />;
      case 'status_changed':
        return <CheckCircle className="h-4 w-4" />;
      case 'assignment_changed':
        return <User className="h-4 w-4" />;
      case 'customer_info_updated':
        return <UserCheck className="h-4 w-4" />;
      case 'priority_changed':
        return <TrendingUp className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getEventColor = (type: string) => {
    switch (type) {
      case 'conversation_started':
        return 'bg-blue-500';
      case 'note_added':
      case 'note_updated':
        return 'bg-yellow-500';
      case 'note_deleted':
        return 'bg-red-500';
      case 'tag_added':
        return 'bg-purple-500';
      case 'tag_removed':
        return 'bg-orange-500';
      case 'status_changed':
        return 'bg-green-500';
      case 'assignment_changed':
        return 'bg-indigo-500';
      case 'customer_info_updated':
        return 'bg-cyan-500';
      case 'priority_changed':
        return 'bg-pink-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4
        className={`text-sm font-semibold uppercase ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}
      >
        Conversation Journey
      </h4>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div
          className={`absolute left-4 top-0 bottom-0 w-0.5 ${
            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
          }`}
        />

        {/* Events */}
        <div className="space-y-4">
          {events.map((event, index) => (
            <div key={event.id} className="relative pl-10">
              {/* Icon */}
              <div
                className={`absolute left-0 flex items-center justify-center w-8 h-8 rounded-full ${getEventColor(
                  event.event_type
                )} text-white`}
              >
                {getEventIcon(event.event_type)}
              </div>

              {/* Content */}
              <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p
                      className={`text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                      }`}
                    >
                      {event.title}
                    </p>
                    {event.description && (
                      <p
                        className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        {event.description}
                      </p>
                    )}
                    {event.created_by_name && (
                      <p
                        className={`text-xs mt-1 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}
                      >
                        by {event.created_by_name}
                      </p>
                    )}
                  </div>
                  <span
                    className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}
                  >
                    {format(new Date(event.created_at), 'MMM d, HH:mm')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {events.length === 0 && (
        <div className="text-center py-8">
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            No journey events yet
          </p>
        </div>
      )}
    </div>
  );
}
